import { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Box, Flex } from '@chakra-ui/react';
import { Avatar, Text, DotsLoader } from '@kula-ai/components';
import type { MentionSuggestion } from '../../api/mentions';

export interface MentionSuggestionsPopupProps {
  open: boolean;
  loading: boolean;
  query: string;
  suggestions: MentionSuggestion[];
  highlightedIndex: number;
  // Anchored to the viewport bottom (distance from bottom edge) so the
  // popup grows upward — caret stays visible, list opens above it.
  position: { bottom: number; left: number } | null;
  onSelect: (_suggestion: MentionSuggestion) => void;
  onHover: (_index: number) => void;
}

const POPUP_WIDTH = 430;
const POPUP_MAX_HEIGHT = 300;
const POPUP_Z = 2000;
// Truncated name/role tooltips portal to <body>; without a higher z they stack
// behind the popup. Keep them just above it.
const TOOLTIP_PROPS = { style: { zIndex: POPUP_Z + 10 } };

// People carry an avatar + role; jobs/flows/other entities render as a plain
// row with the entity name as the right-hand label.
const PEOPLE_ENTITIES = new Set(['contact', 'user']);

const titleCase = (entity: string): string => {
  const trimmed = entity.trim();
  if (trimmed.length === 0) return '';
  return trimmed[0].toUpperCase() + trimmed.slice(1);
};

// Right-side label: people show their role (falling back to the entity name);
// everything else shows the title-cased entity (Job, Flow, Department…).
const rightLabel = (s: MentionSuggestion): string => {
  if (PEOPLE_ENTITIES.has(s.entity)) return s.subtitle || titleCase(s.entity);
  return titleCase(s.entity);
};

export const MentionSuggestionsPopup = ({
  open,
  loading,
  query,
  suggestions,
  highlightedIndex,
  position,
  onSelect,
  onHover,
}: MentionSuggestionsPopupProps) => {
  const listRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open || highlightedIndex < 0) return;
    const active = listRef.current?.querySelector<HTMLElement>(
      `[data-mention-index="${highlightedIndex}"]`
    );
    active?.scrollIntoView({ block: 'nearest' });
  }, [highlightedIndex, open]);

  if (!open || !position) return null;

  // Keep popup inside the viewport horizontally; if the caret is too close
  // to the right edge, shift left so the popup doesn't get clipped.
  const left = Math.min(
    position.left,
    Math.max(8, window.innerWidth - POPUP_WIDTH - 8)
  );

  const content = (
    <Box
      position="fixed"
      bottom={`${position.bottom}px`}
      left={`${left}px`}
      width={`${POPUP_WIDTH}px`}
      maxHeight={`${POPUP_MAX_HEIGHT}px`}
      bg="base-white"
      border="1px solid"
      borderColor="border-color"
      borderRadius="10px"
      boxShadow="0px 8px 24px rgba(38, 34, 68, 0.10)"
      overflow="hidden"
      zIndex={POPUP_Z}
      // Mouse-down on the popup must NOT blur the editor — otherwise we
      // lose the saved caret range before we can insert the mention.
      onMouseDown={(e) => e.preventDefault()}
      data-test-id="mention-suggestions-popup"
    >
      {query.trim() && (
        <Box
          p="12px"
          borderBottom="1px solid"
          borderColor="secondary-50"
          bg="secondary-25"
        >
          <Text variant="p" className="text-secondary-900">
            Results for{' '}
            <Box as="span" className="text-primary-600">
              &ldquo;{query}&rdquo;
            </Box>{' '}
            …
          </Text>
        </Box>
      )}

      {!query.trim() ? (
        <Box px="12px" py="10px">
          <Text variant="p" className="text-secondary-500">
            Directly tag jobs, Acme employees, candidates and more...
          </Text>
        </Box>
      ) : loading && suggestions.length === 0 ? (
        <Flex align="center" justify="center" py="14px">
          <DotsLoader />
        </Flex>
      ) : suggestions.length === 0 ? (
        <Box px="12px" py="10px">
          <Text variant="p" className="text-secondary-500">
            No matches for &ldquo;{query}&rdquo;
          </Text>
        </Box>
      ) : (
        <Box
          ref={listRef}
          maxHeight={`${POPUP_MAX_HEIGHT - 38}px`}
          overflowY="auto"
          py="4px"
          role="listbox"
        >
          {suggestions.map((s, idx) => {
            const active = idx === highlightedIndex;
            const isPerson = PEOPLE_ENTITIES.has(s.entity);
            return (
              <Flex
                key={`${s.entity}-${s.id}`}
                align="center"
                gap="10px"
                px="12px"
                py="7px"
                cursor="pointer"
                bg={active ? 'secondary-50' : 'transparent'}
                _hover={{ bg: 'secondary-50' }}
                onMouseEnter={() => onHover(idx)}
                onClick={() => onSelect(s)}
                role="option"
                aria-selected={active}
                data-mention-index={idx}
                data-test-id={`mention-suggestion-${s.id}`}
                borderBottom="1px solid"
                borderColor="border-color"
              >
                {isPerson && (
                  <Avatar
                    src={s.avatarUrl}
                    name={s.name}
                    shape="square"
                    size={22}
                  />
                )}
                <Text
                  variant="label"
                  as="span"
                  truncate
                  tooltipProps={TOOLTIP_PROPS}
                  className="flex-1"
                >
                  {s.name}
                </Text>
                <Text
                  variant="p"
                  as="span"
                  truncate
                  tooltipProps={TOOLTIP_PROPS}
                  className="text-secondary-500 shrink-0 max-w-[45%] text-right"
                >
                  {rightLabel(s)}
                </Text>
              </Flex>
            );
          })}
        </Box>
      )}
    </Box>
  );

  return createPortal(content, document.body);
};
